import { ABPopupMessageLayout } from '../../../../common/lookups/Layouts/ABLayouts';
import { CodeLookupLayout } from '../../../../common/lookups/Layouts/ImLayouts';

/**
 * Dynamic-form schemas for the WM Item Data Profile screen (IMITEMDP), migrated from the Delphi
 * form uWMD0003. Field labels, group headings and the User Defined code types are taken verbatim
 * from uWMD0003.dfm.
 */

const TABLE = 'IMITEMDP';

// Delphi: ab_AttachPopupCodeLookup(DataModule, edtPOPUPMESSAGE, 'IMITEMDP') - the table name the
// popup messages are keyed by maps straight onto this lookup's tableName param.
const popupMessageLayout = ABPopupMessageLayout({ tableName: TABLE });

// Delphi: the five edtUserDefinedCodeN controls are IMCODES lookups whose CodeType property is
// '28'..'32' respectively (uWMD0003.dfm), with LookupField = 'CODE'.
const userDefinedCodeTypes = ['28', '29', '30', '31', '32'];

// Every coded field below sets searchEnabled: false. Fields.js defaults SelectBox searchEnabled to
// true, which turns the closed input into a type-to-filter search box - the user can delete the
// displayed text and it looks broken, even though acceptCustomValue is false so nothing invalid can
// actually be committed. These lists hold two or three fixed options, so search buys nothing. NOT
// readOnly: that would also stop the dropdown opening, making the field unusable rather than
// pick-only. The `lookup` fields keep their search - they query the server and can return many rows.

/**
 * Mirrors the listing grid's columns one-for-one, including visibility: a column hidden on the list
 * is a hidden field here too, so the dynamic-form customiser exposes exactly the same set either way.
 * RowId is the only grid column left out - it is technical (showInColumnChooser={false}), not a field
 * the user ever sees.
 *
 * Field keys are camelCase while the DTO properties they filter on are PascalCase. FillFilters emits
 * the key verbatim, so this relies on DataSourceLoader resolving the selector case-insensitively -
 * which is the same thing the shipped weekSettings and exceptions search panels rely on ("code" ->
 * Code, "status" -> Status). If a search field ever comes back unfiltered, check that first.
 */
export const SearchLayout = [
    {
        "groupId": 1,
        "fields": [
            { "name": "recordNumber", "localizedField": "KEYSEQUENCE", "tableName": TABLE, "label": "Record #", "type": "text", "required": false, "visible": true, "isLabelChanged": false },
            { "name": "profileCode", "tableName": TABLE, "label": "Profile", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        "groupId": 2,
        "fields": [
            { "name": "description", "tableName": TABLE, "label": "Description", "type": "text", "required": false, "visible": true, "isLabelChanged": false },
            { "name": "addToExpDate", "tableName": TABLE, "label": "Add Days", "type": "text", "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        "groupId": 3,
        "fields": [
            { "name": "storageStartDate", "tableName": TABLE, "label": "Start Date", "type": "select", "searchEnabled": false, "required": false, "visible": true, "isLabelChanged": false },
            { "name": "restartStorageDates", "tableName": TABLE, "label": "Restart Dates", "type": "select", "searchEnabled": false, "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        "groupId": 4,
        "fields": [
            { "name": "expDateBasedOn", "tableName": TABLE, "label": "Based on", "type": "select", "searchEnabled": false, "required": false, "visible": true, "isLabelChanged": false },
            { "name": "rateScheduleFilter", "tableName": TABLE, "label": "Rate Sch Filter", "type": "select", "searchEnabled": false, "required": false, "visible": true, "isLabelChanged": false }
        ]
    },
    {
        // Hidden on the list, so hidden here too.
        "groupId": 5,
        "fields": [
            { "name": "popupMessage", "tableName": TABLE, "label": "Popup Msg", "type": "text", "required": false, "visible": false, "isLabelChanged": false },
            ...userDefinedCodeTypes.map((codeType, index) => ({
                "name": `userDefinedCode${index + 1}`,
                "localizedField": `USERDEFINEDCODE_${index + 1}`,
                "tableName": TABLE,
                "label": `Code ${index + 1}`,
                "type": "lookup",
                "lookupDisplayMode": "grid",
                "layout": CodeLookupLayout({ codeType }),
                "required": false,
                "visible": false,
                "isLabelChanged": false,
            }))
        ]
    }
];

/**
 * The Delphi "Primary" tab.
 *
 * uWMD0003 has only two tabs (Primary and Setup) and puts these fields in an always-visible header
 * panel above them - its own Primary tab is empty. The web form has no such panel, so the header
 * panel's two groups become the Primary tab's two columns, in the same left-to-right order the
 * desktop screen shows them:
 *
 *   left   - Record #, Profile, Description, Popup Msg, Comment  (grpTopLeft, tab order 0,1,2,4)
 *   middle - "User Defined Codes", Code 1..5                     (grpTopMiddle)
 */
export const PrimaryLayout = [
    {
        "groupId": 1,
        "label": "",
        "fields": [
            {
                "name": "RECORD",
                "localizedField": "KEYSEQUENCE",
                "tableName": TABLE,
                "label": "Record #",
                "type": "text",
                "required": false,
                "visible": true,
                "readOnly": true,
                "isLabelChanged": false,
            },
            {
                // "Profile", not "Profile Code": that is what the running Delphi screen shows (the
                // data dictionary overrides the DFM's design-time caption) and what the field's
                // DisplayLabel - and so the listing grid column - already uses.
                "name": "PROFILECODE",
                "tableName": TABLE,
                "label": "Profile",
                "type": "text",
                "required": true,
                "maxLength": 10,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "DESCRIPTION",
                "tableName": TABLE,
                "label": "Description",
                "type": "text",
                "required": false,
                "maxLength": 40,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                "name": "POPUPMESSAGE",
                "tableName": TABLE,
                "label": "Popup Msg",
                "type": "lookup",
                "lookupDisplayMode": "grid",
                "layout": popupMessageLayout,
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                // COMMENT is SQL `text`, so it renders multiline (rule 9), as the Delphi TAbMemo does.
                "name": "COMMENT",
                "tableName": TABLE,
                "label": "Comment",
                "type": "textarea",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    },
    {
        // Delphi grpTopMiddle - the five IMCODES lookups (code types 28..32). IMITEMDP has no
        // user-defined date/quantity/reference columns, so unlike Warehouse Exceptions there is no
        // separate User Defined tab to put them on.
        "groupId": 2,
        "label": "User Defined Codes",
        "fields": userDefinedCodeTypes.map((codeType, index) => ({
            "name": `USERDEFINEDCODE${index + 1}`,
            "localizedField": `USERDEFINEDCODE_${index + 1}`,
            "tableName": TABLE,
            "label": `Code ${index + 1}`,
            "type": "lookup",
            "lookupDisplayMode": "grid",
            "layout": CodeLookupLayout({ codeType }),
            "required": false,
            "visible": true,
            "isLabelChanged": false,
        }))
    }
];

/**
 * The Delphi "Setup" tab. Group labels are the TAbColumnLabel captions from uWMD0003.dfm, kept
 * verbatim so the screen reads the same as the desktop one.
 */
export const SetupLayout = [
    {
        "groupId": 1,
        "label": "Ownership Transfer",
        "fields": [
            {
                "name": "STORAGESTARTDATE",
                "tableName": TABLE,
                "label": "Start Date",
                "type": "select",
                "searchEnabled": false,
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    },
    {
        "groupId": 2,
        "label": "Duplicate Lots - Storage Begin/End Dates",
        "fields": [
            {
                "name": "RESTARTSTORAGEDATES",
                "tableName": TABLE,
                "label": "Re-Initialize",
                "type": "select",
                "searchEnabled": false,
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    },
    {
        "groupId": 3,
        "label": "Expiration date generation",
        "fields": [
            {
                "name": "EXPDATEBASEDON",
                "tableName": TABLE,
                "label": "Based on",
                "type": "select",
                "searchEnabled": false,
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
            {
                // Disabled while Based on is "None", and cleared on the way out - see SetupTab.
                "name": "ADDTOEXPDATE",
                "tableName": TABLE,
                "label": "Add Days",
                "type": "number",
                "format": "#0",
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    },
    {
        "groupId": 4,
        "label": "Rate Schedule",
        "fields": [
            {
                "name": "RATESCHEDULEFILTER",
                "tableName": TABLE,
                "label": "Rate Sch Filter",
                "type": "select",
                "searchEnabled": false,
                "required": false,
                "visible": true,
                "isLabelChanged": false,
            },
        ]
    }
];
